import Eventable from 'utils/eventable';
import utils from 'utils/utils';
import session from './session';
import type { PluginModel } from '../model';
import RelatedPlugin = jwplayer.RelatedPlugin;
import { StreamModel } from '../../model/stream';
type PlaybackEventsOptions = {
    vodInterval?: number;
    liveInterval?: number;
};

type Player = PluginModel['player'];

/**
 * Returns true when custom interval is set to false, null or 0
 */
function isCustomIntervalFalsy(customInterval?: number) {
    return customInterval !== undefined && !customInterval;
}

/**
 * Parse SVP Player events and tailor data to pulse requirements
 *
 * Available events: Play, Pause, Stop, Watch
 */
class PlaybackEvents extends Eventable {
    player: Player;
    watchData: { time: number; duration: number };
    previousTime: number;
    beforeStreamStart?: boolean;
    playMethod: null | string;
    session: (typeof session)[keyof typeof session];
    vodInterval: number | undefined;
    liveInterval: number | undefined;
    previousAssetId: number | null = null;

    constructor(player: Player, options: PlaybackEventsOptions) {
        super();
        this.player = player;
        this.watchData = {
            time: 0,
            duration: -1,
        };

        this.previousTime = -1;

        this.session = this.createSession()!;
        this.beforeStreamStart = true;
        this.playMethod = null;

        this.vodInterval = options?.vodInterval;
        this.liveInterval = options?.liveInterval;

        this.onBeforeUnload = this.onBeforeUnload.bind(this);
        this.onPlaylistItem = this.onPlaylistItem.bind(this);
        this.onAssetPlay = this.onAssetPlay.bind(this);
        this.onPause = this.onPause.bind(this);
        this.onPlay = this.onPlay.bind(this);
        this.onPlayNext = this.onPlayNext.bind(this);
        this.onLiveTime = this.onLiveTime.bind(this);
        this.onTime = this.onTime.bind(this);
        this.onComplete = this.onComplete.bind(this);

        this.listenTo(this.player, 'playlistItem', this.onPlaylistItem);
        this.listenTo(this.player, 'assetPlay', this.onAssetPlay);
        this.listenTo(this.player, 'pause', this.onPause);
        this.listenTo(this.player, 'play', this.onPlay);
        this.listenTo(this.player, 'playNext', this.onPlayNext);
        this.listenTo(this.player, 'seek', this.onSeek);

        if (this.stream?.isLive()) {
            this.listenTo(this.player, 'time', this.onLiveTime);
        } else {
            this.listenTo(this.player, 'time', this.onTime);
        }

        this.listenTo(
            this.player,
            'RecommendedPlugin:nextAssetPlay',
            (
                _asset,
                mode: 'auto' | 'autoplayNext' | 'manualNext',
                opts: { source?: string },
            ) => {
                let playbackSource =
                    mode === 'auto' ? 'autoplayNext' : 'manualNext';
                if (opts.source && opts.source !== 'byId') {
                    playbackSource += `-${opts.source}`;
                }
                return this.onRecommendedPlay(playbackSource);
            },
        );

        this.listenTo(this.player, 'nextAutoAdvance', () =>
            this.onRecommendedPlay('autoplayNext-playlist'),
        );

        this.listenTo(this.player, 'nextClick', () =>
            this.onRecommendedPlay('manualNext-playlist'),
        );

        this.listenTo(this.player, 'relatedReady', () => {
            if (!this.jwRelatedPlugin) {
                return;
            }
            this.jwRelatedPlugin.on('feedClick', () => {
                this.beforeStreamStart = false;
                this.onComplete();
                this.onRecommendedPlay('manualNext-playlist');
            });
        });

        this.listenTo(this.player, 'playlistComplete', () => {
            this.listenToOnce(this.player, 'displayClick', () =>
                this.onRecommendedPlay('manualNext-playlist'),
            );
        });

        this.listenTo(this.player, 'RecommendedPlugin:playRecommended', () => {
            this.onRecommendedPlay('manualNext-grid');
        });

        this.listenToOnce(this.player, 'play', ({ playReason }) => {
            this.playMethod = playReason;
        });

        window.addEventListener(
            'onpagehide' in window.self ? 'pagehide' : 'beforeunload',
            this.onBeforeUnload,
            true,
        );
    }

    get eventData() {
        const data: {
            playMethod: null | string;
            previousAssetId?: number;
            playbackSource?: string;
            duration?: number;
            position?: number;
        } = {
            playMethod: this.playMethod,
        };
        const { previousAssetId } = this.session!;

        if (previousAssetId) {
            data.previousAssetId = previousAssetId;
            data.playbackSource = this.session!.playbackSource;
        }

        data.duration = this.watchData.duration;

        try {
            data.position = Math.floor(this.player.getCurrentTime());
        } catch {
            // could not parse time
            // do not set any value in this case
        }

        return data;
    }

    get stream(): StreamModel | null {
        try {
            return this.player.config.stream;
        } catch {
            // noop
        }
        return null;
    }

    get jw() {
        try {
            return this.player.getJWPlayer();
        } catch {
            // noop
        }
    }

    get jwRelatedPlugin() {
        try {
            return (this.jw?.getPlugin('related') as RelatedPlugin) || null;
        } catch {
            return null;
        }
    }

    getPosition() {
        try {
            return Math.floor(this.player.getCurrentTime());
        } catch {
            return 0;
        }
    }

    createSession() {
        // node is unique player identifier
        const node = this.player.config.get('node')! as { id: string } | string;
        const id = typeof node === 'string' ? node : node.id;

        /**
         * Initialize session object to keep reference
         */
        if (!session[id]) {
            session[id] = {};
        }

        return session[id];
    }

    trackTimeEvent({
        time,
        defaultInterval = 1,
        customInterval,
    }: {
        time: number;
        defaultInterval?: number;
        customInterval?: number;
    }) {
        const eventInterval = utils.isNumber(customInterval)
            ? customInterval
            : defaultInterval;

        // track event data with one second interval
        if (time % 1 === 0 && this.previousTime !== time) {
            this.watchData.duration += 1;
            this.previousTime = time;
        }

        if (time % eventInterval === 0 && this.watchData.time !== time) {
            this.watchData.time = time;

            // skip when custom interval is turned off
            if (isCustomIntervalFalsy(customInterval)) {
                return;
            }

            this.trigger(
                'Watch',
                Object.assign(this.eventData, {
                    position: time,
                    prevPosition: this.previousTime,
                }),
            );
        }
    }

    onBeforeUnload() {
        if (this.beforeStreamStart === false) {
            this.onComplete();
        }
    }

    onPlaylistItem() {
        this.listenToOnce(this.player, 'complete nextClick', this.onComplete);
        const track = this.trigger.bind(
            this,
            'Load',
            Object.assign(this.eventData, {
                position: this.stream?.getPlaybackTime('begin') || 0,
                duration: 0,
            }),
        );

        /**
         * Only first playback has to wait for viewable event
         * Playnext/grid will work without it
         */
        if (this.session.wasViewed === true) {
            track();
        } else {
            // wait for player to be viewable
            this.listenTo(this.jw, 'viewable', (data) => {
                if (data.viewable) {
                    track();
                    this.stopListening(this.jw, 'viewable');
                    this.session.wasViewed = true;
                }
            });
        }

        this.previousAssetId = null;
    }

    onAssetPlay() {
        const intent = this.beforeStreamStart ? 'Start' : 'Resume';
        this.beforeStreamStart = false;

        return this.trigger(
            intent,
            Object.assign(this.eventData, {
                start: true,
                duration: 0,
            }),
        );
    }

    onPause() {
        this.trigger('Pause', this.eventData);
    }

    onPlay() {
        // play can't be triggered before assetPlay
        if (!this.beforeStreamStart) {
            this.trigger('Resume', this.eventData);
        }
    }

    /**
     * Intervals for VOD are defined as follows
     *
     * every 5s - during the first 10 seconds of content playback (no matter of where playback has started)
     * every 10s - for the range (10-60s) of content playback, after seeking intervals should be triggered in same time
     * every 30s - for the rest
     * seek 42, then first trigger should be in 50
     */
    onTime(eventTime: number) {
        const time = Math.floor(eventTime);
        let eventInterval;

        if (eventTime <= 10) {
            eventInterval = 5;
        } else if (eventTime <= 120) {
            eventInterval = 10;
        } else {
            eventInterval = 30;
        }
        this.trackTimeEvent({
            time,
            defaultInterval: eventInterval,
            customInterval: this.vodInterval,
        });
    }

    onSeek(data: {
        currentTime: number;
        duration: number;
        metadata: { currentTime: number };
        offset: number;
        position: number;
        seekRange: { start: number; end: number };
        type: 'seek';
    }) {
        this.listenToOnce(this.player, 'seeked', () => {
            this.trigger(
                'Seek',
                Object.assign(this.eventData, {
                    position: Math.floor(data.offset * 1000),
                    prevPositionMs: Math.floor(data.position * 1000),
                    seekFromMs: Math.floor(data.position * 1000),
                    seekToMs: Math.floor(data.offset * 1000),
                }),
            );
        });
    }

    /**
     * Track live events by checking time from flight time to current date
     * DVR seek is also included in calculations
     */
    onLiveTime() {
        // after playback stars time to start will be negative
        // add DVR shift from playback
        const eventTime = Math.floor(
            (this.stream?.getTimeToStart() || 0) * -1 + this.jw!.getPosition(),
        );
        // constant tracking interval
        this.trackTimeEvent({
            time: eventTime,
            defaultInterval: 30,
            customInterval: this.liveInterval,
        });
    }

    onComplete(reason?: string) {
        if (reason === 'manualNext') {
            this.onRecommendedPlay('manualNext');
        }

        /**
         * Track stop event only when playback has started
         */
        if (this.beforeStreamStart === false) {
            this.trigger('Stop', this.eventData);
            // store asset id in memory to attach it to play next video
            this.session.previousAssetId = this.stream?.getId();
        }
        this.beforeStreamStart = true;
    }

    /**
     * @param {Number} _id
     * @param {Object} [options]
     * @param {Object} [options.pulse]
     * @param {String} [options.source] - A source that will be added as a playback source for pulse metrics
     */
    onPlayNext(
        _id: number,
        options: { pulse: Record<string, unknown>; source: string },
    ) {
        if (options && options.pulse) {
            Object.assign(this.session, options.pulse);
        }
    }

    onRecommendedPlay(playbackSource: string) {
        this.session.playbackSource = playbackSource;
    }

    destroy() {
        window.removeEventListener(
            'onpagehide' in window.self ? 'pagehide' : 'beforeunload',
            this.onBeforeUnload,
            true,
        );
        this.stopListening();
    }
}

export default PlaybackEvents;
